/**
 * Service Worker Update Service
 * Detects when a new service worker version is available (or when server version.json is newer)
 * and triggers update notification so everyone gets onto the same app version.
 */

// Injected at build time (vue.config.js); used to compare with server /version.json
const CLIENT_BUILD_TIME = typeof process.env.VUE_APP_BUILD_TIME !== 'undefined'
  ? Number(process.env.VUE_APP_BUILD_TIME)
  : 0;

// Deploy-style prompts are production-only: in dev, version.json / SW checks fight HMR and restarts.
const PWA_UPDATE_CHECKS_ENABLED = process.env.NODE_ENV === 'production';

const PASSIVE_UPDATE_CHECK_THROTTLE_MS = 5 * 60 * 1000;
const VERSION_LOOP_GUARD_KEY = 'pwa_pending_server_build_time';

class ServiceWorkerUpdateService {
  constructor() {
    this.registration = null;
    this.updateFound = false;
    this.checkingForUpdate = false;
    this.checkingVersion = false;
    this._lastPassiveUpdateCheckAt = 0;
  }

  /**
   * Initialize the service worker update checking
   */
  async initialize() {
    if (!PWA_UPDATE_CHECKS_ENABLED) {
      return;
    }

    if (!('serviceWorker' in navigator)) {
      console.log('Service workers are not supported in this browser');
      return;
    }

    try {
      // Get the service worker registration
      // First try to get existing registration, then wait for ready
      let registration = await navigator.serviceWorker.getRegistration();
      
      if (!registration) {
        // Wait for service worker to be ready (Vue CLI PWA plugin registers it automatically)
        registration = await navigator.serviceWorker.ready;
      }
      
      this.registration = registration;
      
      // Check if there's already a waiting service worker (update already downloaded)
      if (this.registration.waiting) {
        console.log('Found waiting service worker - update available');
        this.handleUpdateFound();
      }
      
      // Set up listeners BEFORE checking for updates
      this.setupUpdateListeners();
      
      // Check for updates immediately
      await this.checkForUpdates();
      
      // Set up periodic update checks
      this.setupPeriodicChecks();
    } catch (error) {
      console.error('Error initializing service worker update service:', error);
    }
  }

  /**
   * Check for service worker updates
   */
  async checkForUpdates() {
    if (!PWA_UPDATE_CHECKS_ENABLED) {
      return;
    }

    // Check server version.json first (catches new deploys even before SW updates)
    await this.checkVersionFromServer();

    if (!this.registration || this.checkingForUpdate) {
      return;
    }

    try {
      this.checkingForUpdate = true;

      console.log('Checking for service worker updates...');

      // Check for updates - this will trigger updatefound event if new version exists
      await this.registration.update();

      // Double-check if there's a waiting service worker (in case update() found one)
      if (this.registration.waiting) {
        console.log('Update found - waiting service worker detected');
        this.handleUpdateFound();
      } else {
        console.log('No updates available');
      }
    } catch (error) {
      console.error('Error checking for service worker updates:', error);
    } finally {
      this.checkingForUpdate = false;
    }
  }

  /**
   * Fetch /version.json (no cache) and compare with client build time.
   * If server has a newer build, trigger update flow so user reloads and gets latest.
   */
  async checkVersionFromServer() {
    if (this.checkingVersion || this.updateFound) {
      return;
    }
    try {
      this.checkingVersion = true;
      const url = `${window.location.origin}/version.json?t=${Date.now()}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      const serverBuildTime = data.buildTime != null ? Number(data.buildTime) : 0;

      if (serverBuildTime > 0 && serverBuildTime <= CLIENT_BUILD_TIME) {
        try {
          sessionStorage.removeItem(VERSION_LOOP_GUARD_KEY);
        } catch (e) {
          // private mode or storage unavailable
        }
      }

      if (serverBuildTime > 0 && serverBuildTime > CLIENT_BUILD_TIME) {
        let pending = '';
        try {
          pending = sessionStorage.getItem(VERSION_LOOP_GUARD_KEY) || '';
        } catch (e) {
          // ignore
        }
        if (pending === String(serverBuildTime)) {
          console.log('Skipping duplicate version prompt for same server build (reload loop guard)');
          return;
        }
        try {
          sessionStorage.setItem(VERSION_LOOP_GUARD_KEY, String(serverBuildTime));
        } catch (e) {
          // ignore
        }
        console.log('New app version on server - triggering update', {
          client: CLIENT_BUILD_TIME,
          server: serverBuildTime
        });
        this.handleUpdateFound();
      }
    } catch (err) {
      // version.json may not exist in dev or network error
    } finally {
      this.checkingVersion = false;
    }
  }

  /**
   * Set up listeners for service worker updates
   */
  setupUpdateListeners() {
    if (!this.registration) {
      return;
    }

    console.log('Setting up service worker update listeners');

    // Listen for when a new service worker is found
    this.registration.addEventListener('updatefound', () => {
      console.log('Service worker update found - new version downloading');
      const newWorker = this.registration.installing;
      
      if (!newWorker) {
        return;
      }

      // Listen for when the new service worker is installed
      newWorker.addEventListener('statechange', () => {
        console.log(`Service worker state changed to: ${newWorker.state}`);
        if (newWorker.state === 'installed') {
          // Check if there's a controller (active service worker)
          // If there is, it means there's an update waiting
          if (navigator.serviceWorker.controller) {
            console.log('New service worker installed and waiting');
            this.handleUpdateFound();
          } else {
            console.log('New service worker installed - this is the first install');
          }
        }
      });
    });

    // Listen for controller change (when new service worker takes control)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // This happens after the page reloads with the new service worker
      console.log('Service worker controller changed - new version active');
      // Reset the update found flag so we can detect future updates
      this.updateFound = false;
    });
  }

  /**
   * Set up periodic checks for updates
   */
  setupPeriodicChecks() {
    const runPassiveCheck = () => {
      const now = Date.now();
      if (now - this._lastPassiveUpdateCheckAt < PASSIVE_UPDATE_CHECK_THROTTLE_MS) {
        return;
      }
      this._lastPassiveUpdateCheckAt = now;
      this.checkForUpdates();
    };

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        console.log('App became visible - checking for updates (throttled)');
        runPassiveCheck();
      }
    });

    window.addEventListener('focus', () => {
      console.log('Window gained focus - checking for updates (throttled)');
      runPassiveCheck();
    });

    setInterval(() => {
      console.log('Periodic update check');
      this.checkForUpdates();
    }, 60 * 1000);
  }

  /**
   * Handle when an update is found
   */
  handleUpdateFound() {
    if (this.updateFound) {
      // Already notified about this update
      console.log('Update already notified - skipping');
      return;
    }

    console.log('Handling update found - dispatching event to app');
    this.updateFound = true;
    
    // Dispatch custom event to notify the app
    const event = new CustomEvent('sw-update-available', {
      detail: {
        registration: this.registration
      }
    });
    window.dispatchEvent(event);
    console.log('Update event dispatched');
  }

  /**
   * Skip waiting and activate the new service worker
   * This is called after the user is notified and the app is about to reload
   */
  async skipWaiting() {
    if (!this.registration || !this.registration.waiting) {
      return;
    }

    try {
      // Send message to waiting service worker to skip waiting
      // Workbox-generated service workers listen for this message
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Also try calling skipWaiting directly if available
      if (this.registration.waiting.skipWaiting) {
        this.registration.waiting.skipWaiting();
      }
    } catch (error) {
      console.error('Error skipping waiting service worker:', error);
    }
  }
}

// Create singleton instance
const serviceWorkerUpdateService = new ServiceWorkerUpdateService();

// Expose globally for debugging (can call window.checkForPWAUpdate() in console)
if (typeof window !== 'undefined') {
  window.checkForPWAUpdate = () => {
    if (!PWA_UPDATE_CHECKS_ENABLED) {
      console.log('PWA update checks are disabled outside production');
      return Promise.resolve();
    }
    console.log('Manual update check triggered');
    return serviceWorkerUpdateService.checkForUpdates();
  };
  
  window.getPWAUpdateStatus = () => {
    return {
      hasRegistration: !!serviceWorkerUpdateService.registration,
      hasWaiting: !!serviceWorkerUpdateService.registration?.waiting,
      hasInstalling: !!serviceWorkerUpdateService.registration?.installing,
      hasActive: !!serviceWorkerUpdateService.registration?.active,
      updateFound: serviceWorkerUpdateService.updateFound
    };
  };
}

export default serviceWorkerUpdateService;
